import React, { useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Sparkles, ChevronRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const STEPS = ["Type", "Details", "AI Draft", "Review"];

const COUNTRIES = ["US", "EU", "IN", "JP", "CN", "DE", "UK", "FR", "KR", "AU", "BR", "AE", "CA"];
const CATEGORIES = [
  "Software & AI",
  "Biotech & Pharma",
  "Medical Devices",
  "Clean Energy",
  "Materials Science",
  "Consumer Brand",
  "Fashion",
  "Automotive",
  "Pharmaceuticals",
  "Literature",
  "Visual Arts",
  "Music & Audio",
  "Other",
];

export default function RegisterIP() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [ipType, setIpType] = useState("patent");
  const [form, setForm] = useState({
    title: "",
    category: "Software & AI",
    country: "US",
    filing_date: new Date().toISOString().split("T")[0],
    filing_number: "",
    abstract: "",
    keywords: "",
    brief: "",
    status: "pending",
  });
  const [draft, setDraft] = useState(null);
  const [drafting, setDrafting] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const runDraft = async () => {
    if (!form.title || !form.keywords) {
      toast.error("Title and keywords required for AI draft");
      return;
    }
    setDrafting(true);
    try {
      const { data } = await api.post("/ai/draft", {
        ip_type: ipType,
        title: form.title,
        keywords: form.keywords,
        brief: form.brief,
      });
      setDraft(data);
      upd("abstract", data.abstract || form.abstract);
      toast.success("AI draft ready");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDrafting(false);
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        title: form.title,
        ip_type: ipType,
        category: form.category,
        country: form.country,
        status: form.status,
        filing_number: form.filing_number || null,
        filing_date: form.filing_date,
        abstract: form.abstract,
        valuation_usd: 0,
      };
      await api.post("/ip/assets", payload);
      toast.success("IP asset registered");
      nav("/portfolio");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-4xl mx-auto fade-in-up" data-testid="register-page">
      <div className="mb-8">
        <div className="eyebrow">Registration Wizard</div>
        <h1 className="font-serif text-4xl sm:text-5xl font-semibold tracking-tight mt-1">
          Register New IP
        </h1>
        <p className="text-slate-600 mt-2 text-sm md:text-base">
          File a copyright, patent, or trademark with AI-assisted drafting.
        </p>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2 mb-8" data-testid="stepper">
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <div className="flex items-center gap-2">
              <div
                className={`stepper-dot text-xs border ${
                  i === step
                    ? "bg-[#B33A3A] text-white border-[#B33A3A]"
                    : i < step
                    ? "bg-[#FAD4D4] text-[#7A1F2B] border-[#FAD4D4]"
                    : "bg-white text-slate-500 border-slate-300"
                }`}
              >
                {i < step ? <CheckCircle2 size={14} /> : i + 1}
              </div>
              <span className={`text-xs font-medium ${i <= step ? "text-slate-900" : "text-slate-400"}`}>{s}</span>
            </div>
            {i < STEPS.length - 1 && <ChevronRight size={14} className="text-slate-300" />}
          </React.Fragment>
        ))}
      </div>

      <div className="bengal-card p-6 md:p-8">
        {/* Step 0: Type */}
        {step === 0 && (
          <div data-testid="step-type">
            <h2 className="font-serif text-2xl mb-5">Select IP type</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { k: "patent", t: "Patent", d: "Inventions, processes, utility & design" },
                { k: "trademark", t: "Trademark", d: "Brand names, logos, word/figurative marks" },
                { k: "copyright", t: "Copyright", d: "Literary, artistic, musical works" },
              ].map((o) => (
                <button
                  key={o.k}
                  onClick={() => setIpType(o.k)}
                  data-testid={`type-${o.k}`}
                  className={`text-left p-5 border rounded-sm transition-all ${
                    ipType === o.k
                      ? "border-[#B33A3A] bg-red-50 shadow-sm"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <div className="font-serif text-xl font-medium">{o.t}</div>
                  <div className="text-xs text-slate-500 mt-1">{o.d}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 1: Details */}
        {step === 1 && (
          <div className="space-y-4" data-testid="step-details">
            <h2 className="font-serif text-2xl mb-3">Core details</h2>
            <div>
              <label className="eyebrow block mb-1.5">Title</label>
              <input value={form.title} onChange={(e) => upd("title", e.target.value)} data-testid="input-title" className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#B33A3A] focus:border-transparent" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="eyebrow block mb-1.5">Category</label>
                <select value={form.category} onChange={(e) => upd("category", e.target.value)} data-testid="input-category" className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm bg-white">
                  {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="eyebrow block mb-1.5">Country</label>
                <select value={form.country} onChange={(e) => upd("country", e.target.value)} data-testid="input-country" className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm bg-white">
                  {COUNTRIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="eyebrow block mb-1.5">Filing Date</label>
                <input type="date" value={form.filing_date} onChange={(e) => upd("filing_date", e.target.value)} data-testid="input-filing-date" className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="eyebrow block mb-1.5">Filing Number (optional)</label>
                <input value={form.filing_number} onChange={(e) => upd("filing_number", e.target.value)} data-testid="input-filing-number" className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm font-mono" />
              </div>
            </div>
            <div>
              <label className="eyebrow block mb-1.5">Keywords</label>
              <input value={form.keywords} onChange={(e) => upd("keywords", e.target.value)} data-testid="input-keywords" placeholder="e.g. quantum, blockchain, consensus" className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="eyebrow block mb-1.5">Inventor brief</label>
              <textarea rows={4} value={form.brief} onChange={(e) => upd("brief", e.target.value)} data-testid="input-brief" placeholder="In plain English, describe what your IP does and why it's novel…" className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm" />
            </div>
          </div>
        )}

        {/* Step 2: AI Draft */}
        {step === 2 && (
          <div data-testid="step-ai-draft">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-serif text-2xl">AI-drafted content</h2>
              <button onClick={runDraft} disabled={drafting} data-testid="ai-draft-btn" className="bengal-btn-primary text-sm flex items-center gap-2 disabled:opacity-50">
                <Sparkles size={15} /> {drafting ? "Drafting…" : draft ? "Regenerate" : "Generate Draft"}
              </button>
            </div>
            {!draft && !drafting && (
              <div className="text-sm text-slate-500 p-6 border border-dashed border-slate-200 rounded-sm text-center">
                Click <em>Generate Draft</em> to have Claude Sonnet 4.5 produce an abstract, claims, and classification.
              </div>
            )}
            {draft && (
              <div className="space-y-5">
                <div>
                  <div className="eyebrow mb-1.5">Abstract</div>
                  <textarea rows={5} value={form.abstract} onChange={(e) => upd("abstract", e.target.value)} data-testid="draft-abstract" className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm" />
                </div>
                {draft.claims && draft.claims.length > 0 && (
                  <div>
                    <div className="eyebrow mb-1.5">Suggested Claims</div>
                    <ol className="list-decimal ml-5 space-y-2 text-sm text-slate-700">
                      {draft.claims.map((c, i) => <li key={i}>{c}</li>)}
                    </ol>
                  </div>
                )}
                {draft.classification && (
                  <div>
                    <div className="eyebrow mb-1.5">Suggested Classification</div>
                    <div className="font-mono text-sm">{draft.classification}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Step 3: Review */}
        {step === 3 && (
          <div data-testid="step-review">
            <h2 className="font-serif text-2xl mb-5">Review & submit</h2>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <dt className="eyebrow">IP Type</dt><dd className="capitalize">{ipType}</dd>
              <dt className="eyebrow">Title</dt><dd>{form.title}</dd>
              <dt className="eyebrow">Category</dt><dd>{form.category}</dd>
              <dt className="eyebrow">Country</dt><dd>{form.country}</dd>
              <dt className="eyebrow">Filing Date</dt><dd>{form.filing_date}</dd>
              <dt className="eyebrow">Filing #</dt><dd className="font-mono">{form.filing_number || "—"}</dd>
              <dt className="eyebrow col-span-2">Abstract</dt>
              <dd className="col-span-2 text-slate-700 leading-relaxed">{form.abstract}</dd>
            </dl>
          </div>
        )}

        {/* Nav */}
        <div className="flex items-center justify-between mt-8 pt-5 border-t border-slate-100">
          <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} data-testid="prev-step-btn" className="text-sm text-slate-600 hover:text-slate-900 disabled:opacity-40">
            ← Back
          </button>
          {step < 3 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={step === 1 && !form.title}
              data-testid="next-step-btn"
              className="bengal-btn-primary text-sm disabled:opacity-50"
            >
              Continue →
            </button>
          ) : (
            <button onClick={submit} disabled={submitting || !form.title} data-testid="submit-registration-btn" className="bengal-btn-primary text-sm disabled:opacity-50">
              {submitting ? "Filing…" : "File IP"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
